import { createTheme } from '@mui/material/styles';

// Factech brand system
const PRIMARY_COLOR = '#F5A623';
const SECONDARY_COLOR = '#D9890D';
/**
 * Escala de superficies.
 *
 * Antes había seis grises distintos repartidos por la aplicación —#1e1e1e,
 * #121212, #2a2a2a, #161616, #1a1a1a, #222222— más los dos que declaraba este
 * tema, que además tiraban a violeta. Una tarjeta al lado de otra no coincidía,
 * y mezclar tonos fríos con cálidos es lo que hacía que el conjunto se viera
 * apagado en vez de negro.
 *
 * Ahora son cuatro escalones de un mismo tono, sin tinte de color: el fondo bien
 * profundo para que las superficies se despeguen, y un salto claro entre cada
 * nivel. Sobre un negro real, el naranja de la marca resalta sin tener que
 * subirle el brillo.
 */
const BACKGROUND_DEFAULT = '#0A0A0C';   // el lienzo
const BACKGROUND_PAPER = '#131316';     // tarjetas y paneles
const SURFACE_RAISED = '#1B1B1F';       // lo que va encima de una tarjeta
const TEXT_PRIMARY = '#F8FAFC';
const TEXT_SECONDARY = '#A8A2AD';
const DIVIDER_COLOR = '#26262C';    // bordes: visibles, sin gritar
const ERROR_COLOR = '#f44336'; 
// Definimos un color para éxito (usado en Register Stepper)
const SUCCESS_COLOR = '#66bb6a'; // Verde estándar de Material UI

/**
 * Las dos familias del sistema.
 *
 * Sora sólo para títulos: tiene carácter y aguanta pesos altos. Inter para todo
 * lo demás, que es donde vive el trabajo — y trae cifras tabulares de verdad,
 * que en un sistema de facturación vale más que cualquier detalle estético:
 * sin ellas, una columna de montos no alinea por la coma y hay que leer número
 * por número.
 */
const DISPLAY = '"Sora", "Manrope", system-ui, sans-serif';
const TEXTO = '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// --- Crea el Tema ---
const theme = createTheme({
  palette: {
    mode: 'dark', 
    primary: {
      main: PRIMARY_COLOR, 
      contrastText: '#1C1525',
    },
    secondary: { 
      main: SECONDARY_COLOR, 
    },
    background: {
      default: BACKGROUND_DEFAULT, 
      paper: BACKGROUND_PAPER,   
    },
    text: {
      primary: TEXT_PRIMARY,     
      secondary: TEXT_SECONDARY, 
    },
    divider: DIVIDER_COLOR,
    error: {
      main: ERROR_COLOR,
    },
    success: { // Añadimos el color de éxito a la paleta
        main: SUCCESS_COLOR,
        contrastText: '#000000', // Texto oscuro sobre verde claro
    },
    // Definimos colores para action.disabled y action.disabledBackground
    // que se usarán automáticamente por los componentes MUI en estado disabled
    action: {
        disabledBackground: 'rgba(245, 166, 35, 0.22)',
        disabled: 'rgba(255, 255, 255, 0.5)', // Texto blanco con opacidad
        hover: 'rgba(245, 166, 35, 0.08)',
        selected: 'rgba(245, 166, 35, 0.14)',
        active: TEXT_SECONDARY,
    }
  },
  typography: {
    fontFamily: TEXTO,
    /**
     * Escala tipográfica.
     *
     * Antes cada título elegía su tamaño por su cuenta y salían valores como
     * 36,8 o 25,6 píxeles, que no son de ninguna escala: se llegaba ahí
     * multiplicando rems a ojo. Peor, casi todo el sistema usaba `h6` con
     * tamaños de 12 a 20 px, así que el nivel del título no decía nada sobre su
     * importancia — un encabezado de tabla y el título de una sección eran el
     * mismo elemento.
     *
     * Esto es una escala de cuarta justa (×1.333) redondeada a valores enteros.
     * Cada nivel se ve claramente distinto del anterior, que es todo lo que se
     * le pide a una jerarquía.
     */
    h1: { fontFamily: DISPLAY, fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.15, letterSpacing: '-0.02em' },
    h2: { fontFamily: DISPLAY, fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.2, letterSpacing: '-0.015em' },
    h3: { fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25, letterSpacing: '-0.01em' },
    h4: { fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
    h5: { fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.35, marginBottom: '1rem' },
    h6: { fontFamily: TEXTO, fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.4 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem', letterSpacing: '0.02em' },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: {
        color: TEXT_SECONDARY,
        fontSize: '0.875rem',
        lineHeight: 1.5,
    },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
    button: {
      textTransform: 'none', 
      fontWeight: 600,       
    },
  },
  components: {
    MuiCssBaseline: { 
        styleOverrides: {
            body: {
                backgroundColor: BACKGROUND_DEFAULT,
                backgroundImage: 'radial-gradient(circle at 85% 0%, rgba(245, 166, 35, 0.035), transparent 32%)',
            },
        },
    },
    /**
     * Cabecera de tabla.
     *
     * Estaba pintada de naranja sólido a todo lo ancho, así que lo que más
     * gritaba en la pantalla de facturas era el rótulo de las columnas, no las
     * facturas. En un sistema donde la gente viene a leer datos, el cromo tiene
     * que desaparecer: mayúsculas chicas, gris, y una línea que separe. Nada más.
     */
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'transparent',
            color: TEXT_SECONDARY,
            fontFamily: TEXTO,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${DIVIDER_COLOR}`,
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
    },

    /**
     * Cifras tabulares en toda celda de tabla.
     *
     * Sin esto, el 1 ocupa menos que el 8 y una columna de montos queda con las
     * comas bailando: hay que leer número por número en vez de comparar de un
     * vistazo. Es el detalle que más separa un sistema contable de una tabla
     * cualquiera, y cuesta una línea.
     */
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontVariantNumeric: 'tabular-nums',
          borderBottom: `1px solid ${DIVIDER_COLOR}`,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', 
          border: `1px solid ${DIVIDER_COLOR}`,
        },
        rounded: {
           borderRadius: 12, 
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          minHeight: 40,
          padding: '9px 18px',
          boxShadow: 'none',
          '&.MuiButton-containedPrimary': {
            background: PRIMARY_COLOR,
            color: '#1C1525',
            '&:hover': {
              background: '#E59615',
              boxShadow: '0 3px 10px rgba(245, 166, 35, 0.12)',
            },
          },
          // Corrección: Usamos '&.Mui-disabled' para sobrescribir el estado disabled
          '&.Mui-disabled': {
            // Usamos los colores definidos en palette.action
            backgroundColor: `rgba(${parseInt(PRIMARY_COLOR.slice(1, 3), 16)}, ${parseInt(PRIMARY_COLOR.slice(3, 5), 16)}, ${parseInt(PRIMARY_COLOR.slice(5, 7), 16)}, 0.3)`, 
            color: 'rgba(255, 255, 255, 0.5)', 
            cursor: 'not-allowed',
            pointerEvents: 'auto', 
          }
        },
        // Los estilos específicos para containedPrimary, etc., pueden ir aquí si es necesario
        // pero el estilo base del degradado se aplica con 'sx' en el componente.
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined', 
      },
      styleOverrides: {
        root: {
          '& label': {
            color: TEXT_SECONDARY, 
          },
          '& label.Mui-focused': {
            color: PRIMARY_COLOR, 
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: TEXT_SECONDARY, 
            },
            '&:hover fieldset': {
               borderColor: TEXT_PRIMARY, 
            },
            '&.Mui-focused fieldset': {
              borderColor: PRIMARY_COLOR, 
            },
            '& input': {
                color: TEXT_PRIMARY,
            },
            // Estilo para el input deshabilitado
            '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)', // Fondo ligeramente diferente
                '& fieldset': {
                    borderColor: `rgba(${parseInt(TEXT_SECONDARY.slice(1, 3), 16)}, ${parseInt(TEXT_SECONDARY.slice(3, 5), 16)}, ${parseInt(TEXT_SECONDARY.slice(5, 7), 16)}, 0.5)`, // Borde más tenue
                },
                 '& input': {
                    // Color del texto deshabilitado (usará palette.text.disabled por defecto si no se especifica)
                 },
            }
          },
        },
      },
    },
     MuiLink: {
      styleOverrides: {
        root: {
          color: SECONDARY_COLOR, 
          textDecoration: 'none', 
          '&:hover': {
            textDecoration: 'underline', 
            textDecorationColor: SECONDARY_COLOR, 
          }
        },
      },
    },
    MuiAlert: { 
        styleOverrides: {
            root: {
                borderRadius: 8, 
            },
            standardError: { 
                backgroundColor: `rgba(${parseInt(ERROR_COLOR.slice(1, 3), 16)}, ${parseInt(ERROR_COLOR.slice(3, 5), 16)}, ${parseInt(ERROR_COLOR.slice(5, 7), 16)}, 0.1)`, 
                color: ERROR_COLOR, 
                border: `1px solid ${ERROR_COLOR}`, 
            },
            // Añadimos estilo para alerta de éxito
            standardSuccess: {
                backgroundColor: `rgba(${parseInt(SUCCESS_COLOR.slice(1, 3), 16)}, ${parseInt(SUCCESS_COLOR.slice(3, 5), 16)}, ${parseInt(SUCCESS_COLOR.slice(5, 7), 16)}, 0.2)`, // Fondo verde suave 
                color: '#a5d6a7', // Texto verde claro (ajustar si es necesario)
                border: `1px solid #a5d6a7`, // Borde verde claro
                 '& .MuiAlert-icon': { // Color del icono
                    color: '#a5d6a7', // Ajustar si es necesario
                }
            }
        }
    },
    MuiCircularProgress: { 
        styleOverrides: {
            root: {
                // Heredará el color del texto del botón (blanco)
            }
        }
    },
    // Añadimos estilos para el Stepper para que se vea bien en oscuro
    MuiStepLabel: {
        styleOverrides: {
            label: {
                color: TEXT_SECONDARY, // Color por defecto de la etiqueta
                '&.Mui-active': {
                    color: TEXT_PRIMARY, // Color activo
                    fontWeight: 'bold',
                },
                '&.Mui-completed': {
                    color: TEXT_PRIMARY, // Color completado
                    fontWeight: 'normal',
                },
            },
        }
    },
    MuiStepIcon: {
        styleOverrides: {
            root: {
                color: TEXT_SECONDARY, // Color por defecto del icono
                '&.Mui-active': {
                    color: PRIMARY_COLOR, // Color activo (azul primario)
                },
                '&.Mui-completed': {
                    color: SUCCESS_COLOR, // Color completado (verde éxito)
                },
            },
        }
    }
  },
});

export default theme;
